import React, { useEffect, useState } from "react";
import {
  Card,
  Form,
  Input,
  Button,
  Radio,
  Drawer,
  Table,
  DatePicker,
  Spin,
  Select,
} from "antd";
import { EditOutlined, DeleteOutlined, EyeOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  useCreateSupplierMutation,
  useDeleteSupplierMutation,
  useFetchSupplieresQuery,
  useUpdateSupplierMutation,
} from "./supplier.service";
import moment from "moment/moment";

const { Column } = Table;

const options = [
  {
    value: "level1",
    label: "level1",
  },
  {
    value: "level2",
    label: "level2",
  },
  {
    value: "level3",
    label: "level3",
  },
];

export default function SupplierPage() {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const [form] = Form.useForm();
  const [isDrawer, setIsDrawer] = useState(false);
  const [isToolLoading, setIsToolLoading] = useState("");
  const [isEditable, setIsEditable] = useState();

  const {
    data: supplieres = [],
    isLoading,
    refetch,
  } = useFetchSupplieresQuery("", {
    refetchOnMountOrArgChange: true,
  });

  const [requestCreateSupplier, { isSuccess, isLoading: isCreateLoading }] =
    useCreateSupplierMutation();
  const [requestUpdateSupplier, { isSuccess: isUpdateSuccess }] =
    useUpdateSupplierMutation();

  const [requestDeleteSupplier, { isSuccess: isDeleteSuccess }] =
    useDeleteSupplierMutation();

  const handleDrawer = () => {
    setIsDrawer(true);
  };

  const handleDeleteSupplier = async (id) => {
    setIsToolLoading(id);
    await requestDeleteSupplier(id);
    setIsToolLoading("");
  };

  const handleUpdateSupplier = (data) => {
    setIsEditable(data._id);
    setIsDrawer(true);
    form.setFieldsValue({
      businessName: data.businessName,
      contactName: data.contactName,
      telephone: data.telephone,
      email: data.email,
      address: data.address,
    });
  };

  useEffect(() => {
    if (!isDrawer) {
      form.resetFields();
      setIsEditable();
    }
  }, [isDrawer]);

  useEffect(() => {
    if (isSuccess) refetch();
  }, [isCreateLoading]);

  const handleFormSubmit = (data) => {
    if (isEditable) requestUpdateSupplier({ id: isEditable, data });
    else requestCreateSupplier(data);
    setIsDrawer(false);
  };

  const handleView = (id) => {
    navigate("/supplier/" + id);
  };

  return (
    <Card
      className="team-page"
      bordered={false}
      title={t("SUPPLIERS LIST")}
      extra={
        <Button type="primary" onClick={handleDrawer}>
          {t("Create")}
        </Button>
      }
    >
      <Spin spinning={isLoading}>
        <div className="team-page-container">
          <div className="team-page-container-content">
            <Table dataSource={supplieres} pagination={true}>
              <Column
                align="center"
                title={t("Business Name")}
                dataIndex={"businessName"}
              />
              <Column
                align="center"
                title={t("Contact Name")}
                dataIndex={"contactName"}
              />
              <Column
                align="center"
                title={t("Telephone")}
                dataIndex={"telephone"}
              />
              <Column align="center" title={t("Email")} dataIndex={"email"} />
              <Column
                align="center"
                title={t("Address")}
                dataIndex={"address"}
              />
              <Column
                title={t("TOOLS")}
                align="center"
                render={(value, record, index) => (
                  <>
                    <Button
                      className="team-page-container-content-tool"
                      color="green"
                      variant="solid"
                      icon={<EditOutlined />}
                      size="small"
                      isLoading={isToolLoading === record._id ? true : false}
                      onClick={() => handleUpdateSupplier(record)}
                    >
                      {t("Edit")}
                    </Button>
                    <Button
                      className="team-page-container-content-tool"
                      variant="solid"
                      color="danger"
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={() => handleDeleteSupplier(record._id)}
                      isLoading={isToolLoading === record._id ? true : false}
                    >
                      {t("DELETE")}
                    </Button>
                  </>
                )}
              />
            </Table>
          </div>

          <Drawer
            title={t("Add new Supplier")}
            width={"70%"}
            open={isDrawer}
            onClose={() => {
              form.resetFields();
              setIsDrawer(false);
            }}
          >
            <Form
              form={form}
              layout="vertical"
              onFinish={handleFormSubmit}
              initialValues={{
                businessName: "",
                contactName: "",
                telephone: "",
                email: "",
                address: "",
              }}
            >
              <Form.Item
                name="businessName"
                label={t("Business Name")}
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="contactName"
                label={t("Contact Name")}
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="telephone"
                label={t("Telephone")}
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="email"
                label={t("Email")}
                rules={[{ required: true, type: "email" }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="address"
                label={t("Address")}
                rules={[{ required: true }]}
              >
                <Input.TextArea rows={2} />
              </Form.Item>

              <div className="team-page-container-form-button">
                <Button type="primary" htmlType="submit">
                  {isEditable ? t("UPDATE") : t("CREATE")}
                </Button>
              </div>
            </Form>
          </Drawer>
        </div>
      </Spin>
    </Card>
  );
}
